<script setup>
import {__} from '../lib/localization';
import {scrollTo} from '../lib/patch.js';
import {useForm} from "@straylightagency/form";
import {onMounted, ref} from "vue";
import {slideHide, slideShow, fadeShow, fadeHide} from "@straylightagency/animate";
import {useRequest} from "@straylightagency/http-client";
import {getElementSizing} from "@straylightagency/utils";

const form = useForm('post', '/api/v1/quotation', {
    need: '',
    budget: '',
    timeline: '',
    project_description: '',
    current_website: '',
    name: '',
    company: '',
    email: '',
    phone: '',
    referral_source: '',
    lang: window.lang,
});

const processing = ref(false);
const rootEl = ref();
const confirmEl = ref();
const formEl = ref();
const selectOptions = ref({});
const errors = ref({});

onMounted( async () => {
    const {body} = await useRequest( '/api/v1/quotation-select-options?lang=' + window.lang ).get();

    selectOptions.value = body.data;
} );

async function handleClick() {
    errors.value = {};
    processing.value = true;

    if ( window.grecaptcha !== undefined ) {
        const recaptcha = await grecaptcha.execute( window.recaptcha_site_key , { action: 'QUOTATION' } );
        form.set( 'captcha', 'test' );
    } else {
        form.set( 'captcha', 'fake captcha token' );
    }

    const response = await form.submit();

    const sizing = getElementSizing( rootEl.value );
    await scrollTo( window.scrollY + sizing.top - 300 );

    form.reset();

    processing.value = false;

    if ( response.status >= 200 && response.status <= 299 ) {
        await slideHide( formEl.value, 500 );

        await fadeShow( confirmEl.value, 500, undefined, 'flex' );

        setTimeout( async () => {
            await fadeHide( confirmEl.value, 500 );
            await slideShow( formEl.value, 500 );
        }, 15_000 );
    } else {
        errors.value = response.body.errors;
    }
}

</script>

<template>
    <div ref="rootEl">
        <div class="w-4/9 m-1/9 px-2 flex flex-row mb-6" style="display:none;" ref="confirmEl">
            <div class="flex-none w-auto mr-4">
                <i class="fa-fw fa-solid fa-check text-white text-6xl"></i>
            </div>

            <div class="flex-none shrink">
                <p class="text-white uppercase font-normal relative block mb-5
                            after:content-[''] after:w-20 after:h-0.5 after:bg-white after:absolute after:left-0 after:-bottom-2"
                >{{ __('quotation.confirmation.title') }}</p>

                <p class="text-white/75 text-sm">{{ __('quotation.confirmation.text') }}</p>
            </div>
        </div>

        <form method="post" class="sm:m-1/9 sm:w-7/9 md:w-6/9 lg:w-5/9 xl:w-4/9 px-2" ref="formEl">
            <div class="flex flex-col mb-24">
                <h2 class="h2">
                    <strong>01.</strong>{{ __('quotation.01_title') }}
                </h2>

                <div class="flex flex-col mb-10">
                    <label for="input_need" class="text-white mb-4">{{ __('quotation.your_need') }} <span class="text-red-500">*</span></label>
                    <select name="need" id="input_need" class="text-white bg-cosmic-gray/50 border border-space-gray/75 p-2 focus:outline-none focus:ring-[1px] focus:ring-pale-gold" v-model="form.need">
                        <option v-for="(label, key) of selectOptions.needs" :value="key">{{ label }}</option>
                    </select>
                    <span class="mt-2 text-xs text-red-500" v-if="errors.need && errors.need.length > 0">{{ errors.need[0] }}</span>
                </div>

                <div class="flex flex-col mb-10">
                    <label for="input_budget" class="text-white mb-4">{{ __('quotation.your_budget') }} <span class="text-red-500">*</span></label>
                    <select name="budget" id="input_budget" class="text-white bg-cosmic-gray/50 border border-space-gray/75 p-2 focus:outline-none focus:ring-[1px] focus:ring-pale-gold" v-model="form.budget">
                        <option v-for="(label, key) of selectOptions.budgets" :value="key">{{ label }}</option>
                    </select>
                    <span class="mt-2 text-xs text-red-500" v-if="errors.budget && errors.budget.length > 0">{{ errors.budget[0] }}</span>
                </div>

                <div class="flex flex-col mb-10">
                    <label for="input_timeline" class="text-white mb-4">{{ __('quotation.your_timeline') }} <span class="text-red-500">*</span></label>
                    <select name="timeline" id="input_timeline" class="text-white bg-cosmic-gray/50 border border-space-gray/75 p-2 focus:outline-none focus:ring-[1px] focus:ring-pale-gold" v-model="form.timeline">
                        <option v-for="(label, key) of selectOptions.timelines" :value="key">{{ label }}</option>
                    </select>
                    <span class="mt-2 text-xs text-red-500" v-if="errors.timeline && errors.timeline.length > 0">{{ errors.timeline[0] }}</span>
                </div>

                <div class="flex flex-col mb-10">
                    <label for="input_description" class="text-white mb-4">{{ __('quotation.describe_your_project') }} <span class="text-red-500">*</span></label>
                    <textarea class="text-white bg-cosmic-gray/50 border border-space-gray/75 h-40 resize-y p-2 focus:outline-none focus:ring-[1px] focus:ring-pale-gold" id="input_description" name="project_description" v-model="form.project_description"></textarea>
                    <span class="mt-2 text-xs text-red-500" v-if="errors.project_description && errors.project_description.length > 0">{{ errors.project_description[0] }}</span>
                </div>

                <div class="flex flex-col mb-10">
                    <label for="input_current_website" class="text-white mb-4">{{ __('quotation.current_website') }}</label>
                    <input type="text" class="text-white bg-cosmic-gray/50 border border-space-gray/75 p-2 focus:outline-none focus:ring-[1px] focus:ring-pale-gold" id="input_current_website" name="current_website" v-model="form.current_website"/>
                </div>
            </div>

            <div>
                <h2 class="h2">
                    <strong>02.</strong> {{ __('quotation.02_title') }}
                </h2>

                <div class="flex flex-col mb-10">
                    <label for="input_name" class="text-white mb-4">{{ __('quotation.your_name') }} <span class="text-red-500">*</span></label>
                    <input type="text" class="text-white bg-cosmic-gray/50 border border-space-gray/75 p-2 focus:outline-none focus:ring-[1px] focus:ring-pale-gold" id="input_name" name="name" v-model="form.name"/>
                    <span class="mt-2 text-xs text-red-500" v-if="errors.name && errors.name.length > 0">{{ errors.name[0] }}</span>
                </div>

                <div class="flex flex-col mb-10">
                    <label for="input_company" class="text-white mb-4">{{ __('quotation.your_company') }} <span class="text-red-500">*</span></label>
                    <input type="text" class="text-white bg-cosmic-gray/50 border border-space-gray/75 p-2 focus:outline-none focus:ring-[1px] focus:ring-pale-gold" id="input_company" name="company" v-model="form.company"/>
                    <span class="mt-2 text-xs text-red-500" v-if="errors.company && errors.company.length > 0">{{ errors.company[0] }}</span>
                </div>

                <div class="flex flex-col mb-10">
                    <label for="input_email" class="text-white mb-4">{{ __('quotation.your_email') }} <span class="text-red-500">*</span></label>
                    <input type="text" class="text-white bg-cosmic-gray/50 border border-space-gray/75 p-2 focus:outline-none focus:ring-[1px] focus:ring-pale-gold" id="input_email" name="email" v-model="form.email"/>
                    <span class="mt-2 text-xs text-red-500" v-if="errors.email && errors.email.length > 0">{{ errors.email[0] }}</span>
                </div>

                <div class="flex flex-col mb-10">
                    <label for="input_phone" class="text-white mb-4">{{ __('quotation.your_phone') }}</label>
                    <input type="text" class="text-white bg-cosmic-gray/50 border border-space-gray/75 p-2 focus:outline-none focus:ring-[1px] focus:ring-pale-gold" id="input_phone" name="phone" v-model="form.phone"/>
                </div>

                <div class="flex flex-col mb-10">
                    <label for="input_how" class="text-white mb-4">{{ __('quotation.how_did_you_know_us') }} <span class="text-red-500">*</span></label>
                    <select name="referral_source" id="input_how" class="text-white bg-cosmic-gray/50 border border-space-gray/75 p-2 focus:outline-none focus:ring-[1px] focus:ring-pale-gold" v-model="form.referral_source">
                        <option v-for="(label, key) of selectOptions.referral_sources" :value="key">{{ label }}</option>
                    </select>
                    <span class="mt-2 text-xs text-red-500" v-if="errors.referral_source && errors.referral_source.length > 0">{{ errors.referral_source[0] }}</span>
                </div>

                <div class="flex flex-col">
                    <div>
                        <button type="submit" class="button | w-auto relative" @click.prevent="handleClick" :class="{
                                disabled: processing
                            }" :title="__('quotation.send')">
                            <span class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" v-if="processing">
                                <i class="fa-solid fa-spinner fa-spin"></i>
                            </span>
                            <span :class="{
                                'opacity-0': processing
                            }">
                                <i class="fa-solid fa-arrow-right | mr-2"></i>
                                {{ __('quotation.send') }}
                            </span>
                            <span class="corner-top-left corner-top-right"></span>
                            <span class="corner-bottom-left corner-bottom-right"></span>
                        </button>
                    </div>
                    <div>
                        <span class="mt-2 text-xs text-red-500" v-if="errors.recaptcha && errors.recaptcha.length > 0">{{ errors.recaptcha[0] }}</span>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>